/**
 * ActivationService (AJIP-16.02.006 · WS8)
 * ---------------------------------------------------------------------
 * The single Journey Activation Service.
 *
 * Founder Rulings & Directives applied:
 *   A1  — Activation independently revalidates conflicts before
 *         activation (defence in depth).
 *   A2  — ActivationResult exposes NO Core Engine object.
 *   A3  — Idempotency is in-memory only.
 *   A6  — Deterministic clock injection.
 *   Add — Activation Authorisation Gate is a protected component.
 *   Add — Activation remains deterministic; AI may recommend, AI shall
 *         never activate.
 *
 * The service NEVER:
 *   * Modifies the Core Engine.
 *   * Registers journeys.
 *   * Persists directly.
 *   * Renders UI.
 *   * Navigates the customer.
 *   * Calls providers.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { ResolutionService } from './ResolutionService';
import { IdempotencyStore } from './IdempotencyStore';
import { assertActivationAuthorised } from './ActivationAuthorisationGate';
import {
  plan as planRestoration,
  execute as executeRestoration,
  ACTIONS as RESTORE_ACTIONS,
} from './RestorationResolver';
import { detect as detectConflict } from './ConflictDetector';
import {
  RESOLUTION_DECISIONS,
  isResolvedForActivation,
  isResolvedForRestoration,
} from './ResolutionDecisionContract';
import {
  ACTIVATION_STATUSES,
  IDEMPOTENCY_STATUSES,
  RESTORATION_STATUSES,
  makeActivationResult,
  buildJourneyInstanceId,
} from './ActivationResultContract';
import {
  RESOLUTION_ACTIVATION_EVENTS,
  ResolutionActivationObservability,
} from './ResolutionActivationObservability';
import {
  ActivationConflictError,
  DuplicateActivationError,
} from './ResolutionActivationErrors';

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_ENABLED is false. ActivationService is inert.');
};

class ActivationService {
  constructor({
    engine,
    resolutionService,
    persistenceService = null,
    idempotencyStore = null,
    observability = null,
    nowFn = () => Date.now(),
  } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    if (this._enabled) {
      if (!engine) throw new Error('ActivationService requires an engine reference');
      if (!(resolutionService instanceof ResolutionService)) {
        throw new Error('ActivationService requires a ResolutionService');
      }
    }
    this._engine = engine || null;
    this._resolution = resolutionService || null;
    this._persistence = persistenceService;
    this._idempotency = idempotencyStore instanceof IdempotencyStore
      ? idempotencyStore
      : new IdempotencyStore({ nowFn });
    this._observability = observability instanceof ResolutionActivationObservability
      ? observability
      : (resolutionService && resolutionService.observability instanceof ResolutionActivationObservability
        ? resolutionService.observability
        : new ResolutionActivationObservability());
    this._now = nowFn;
  }

  get enabled() { return this._enabled; }
  get observability() { return this._observability; }
  get idempotencyStore() { return this._idempotency; }

  _emit(kind, payload) { this._observability.emit(kind, payload); }

  _rejectedResult({ request, resolutionDecision, status, reason, restorationStatus = RESTORATION_STATUSES.NOT_APPLICABLE }) {
    return makeActivationResult({
      requestId: request && request.requestId,
      resolutionDecisionId: resolutionDecision && resolutionDecision.resolutionDecisionId,
      journeyId: resolutionDecision && resolutionDecision.journeyId,
      journeyVersion: resolutionDecision && resolutionDecision.journeyVersion,
      status,
      principalRef: (request && request.principalContext && request.principalContext.principalRef) || null,
      idempotencyStatus: IDEMPOTENCY_STATUSES.FIRST,
      conflictStatus: (resolutionDecision && resolutionDecision.conflictStatus) || 'none',
      restorationStatus,
      reason,
      nowFn: this._now,
    });
  }

  async activate(request) {
    _requireEnabled(this);
    this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_REQUESTED, {
      requestId: request && request.requestId,
    });

    // WS10 — Idempotency replay.
    const requestId = request && request.requestId;
    if (typeof requestId === 'string' && this._idempotency.has(requestId)) {
      const prior = this._idempotency.get(requestId);
      this._emit(RESOLUTION_ACTIVATION_EVENTS.IDEMPOTENT_REQUEST_REPLAYED, {
        requestId, priorStatus: prior.status,
      });
      // Return the prior result flagged as REPLAYED.
      return makeActivationResult({
        ...prior,
        idempotencyStatus: IDEMPOTENCY_STATUSES.REPLAYED,
        nowFn: this._now,
      });
    }

    // WS10 — Simultaneous activation guard.
    try { this._idempotency.beginInFlight(requestId); }
    catch (e) {
      if (e instanceof DuplicateActivationError) {
        this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_REJECTED, {
          requestId, reason: 'DUPLICATE_IN_FLIGHT',
        });
        return this._rejectedResult({
          request, resolutionDecision: null,
          status: ACTIVATION_STATUSES.CONFLICT,
          reason: 'DUPLICATE_IN_FLIGHT',
        });
      }
      throw e;
    }

    try {
      // WS3 — Resolution (single authority).
      const resolutionDecision = await this._resolution.resolve(request);

      const activatable = isResolvedForActivation(resolutionDecision);
      const restorable = isResolvedForRestoration(resolutionDecision);

      if (!activatable && !restorable) {
        // Map ResolutionDecision → ActivationResult reject status.
        const statusMap = {
          [RESOLUTION_DECISIONS.INVALID_REQUEST]: ACTIVATION_STATUSES.REJECTED,
          [RESOLUTION_DECISIONS.NOT_FOUND]: ACTIVATION_STATUSES.REJECTED,
          [RESOLUTION_DECISIONS.INELIGIBLE]: ACTIVATION_STATUSES.REJECTED,
          [RESOLUTION_DECISIONS.FEATURE_DISABLED]: ACTIVATION_STATUSES.FEATURE_DISABLED,
          [RESOLUTION_DECISIONS.PERMISSION_DENIED]: ACTIVATION_STATUSES.PERMISSION_DENIED,
          [RESOLUTION_DECISIONS.MISSING_PREREQUISITE]: ACTIVATION_STATUSES.REJECTED,
          [RESOLUTION_DECISIONS.VERSION_UNSUPPORTED]: ACTIVATION_STATUSES.REJECTED,
          [RESOLUTION_DECISIONS.DEPRECATED]: ACTIVATION_STATUSES.REJECTED,
          [RESOLUTION_DECISIONS.CONFLICT]: ACTIVATION_STATUSES.CONFLICT,
          [RESOLUTION_DECISIONS.ACTIVATION_PROHIBITED]: ACTIVATION_STATUSES.BLOCKED,
        };
        const status = statusMap[resolutionDecision.decision] || ACTIVATION_STATUSES.REJECTED;
        const result = this._rejectedResult({
          request, resolutionDecision, status,
          reason: resolutionDecision.reason,
        });
        this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_REJECTED, {
          requestId, decision: resolutionDecision.decision, reason: resolutionDecision.reason,
        });
        this._idempotency.set(requestId, result);
        return result;
      }

      // WS7 — Activation Authorisation Gate (protected component).
      const definition = (this._resolution && this._resolution._discoveryService)
        ? this._resolution._discoveryService.getById(resolutionDecision.journeyId)
        : null;
      try {
        assertActivationAuthorised({
          request,
          resolutionDecision,
          definition,
          engine: this._engine,
          registrationService: this._resolution && this._resolution._registrationService,
          discoveryService: this._resolution && this._resolution._discoveryService,
          persistenceService: this._persistence,
          nowFn: this._now,
        });
      } catch (gateErr) {
        this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_REJECTED, {
          requestId, gateError: gateErr.name, message: gateErr.message,
        });
        const status = gateErr.name === 'PermissionDeniedError'
          ? ACTIVATION_STATUSES.PERMISSION_DENIED
          : (gateErr.name === 'ActivationConflictError'
            ? ACTIVATION_STATUSES.CONFLICT
            : (gateErr.name === 'CoreEngineUnavailableError'
              ? ACTIVATION_STATUSES.FAILED
              : ACTIVATION_STATUSES.REJECTED));
        const result = this._rejectedResult({
          request, resolutionDecision, status,
          reason: gateErr.message,
        });
        this._idempotency.set(requestId, result);
        return result;
      }
      this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_AUTHORISED, {
        requestId, journeyId: resolutionDecision.journeyId,
      });

      // WS6 — Defence in depth: independent revalidation of conflicts
      // immediately before activation (Founder Ruling A1).
      const preflight = detectConflict({
        request, definition,
        engine: this._engine,
        registrationService: this._resolution && this._resolution._registrationService,
        idempotencyStore: null, // request already claimed above
      });
      if (preflight.hasConflict) {
        this._emit(RESOLUTION_ACTIVATION_EVENTS.CONFLICT_DETECTED, {
          requestId, journeyId: resolutionDecision.journeyId, kind: preflight.kind, phase: 'activation-preflight',
        });
        const result = this._rejectedResult({
          request, resolutionDecision,
          status: ACTIVATION_STATUSES.CONFLICT,
          reason: preflight.kind,
        });
        this._idempotency.set(requestId, result);
        return result;
      }

      // WS9 — Execute new activation, resume, or restore.
      const restorePlan = await planRestoration({
        engine: this._engine,
        persistenceService: this._persistence,
        request,
        definition,
      });

      let status = ACTIVATION_STATUSES.ACTIVATED;
      let restorationStatus = RESTORATION_STATUSES.NOT_APPLICABLE;
      let initialState = null;
      try {
        if (restorePlan.action === RESTORE_ACTIONS.RESUME_INMEMORY) {
          this._emit(RESOLUTION_ACTIVATION_EVENTS.JOURNEY_RESTORATION_REQUESTED, {
            requestId, journeyId: resolutionDecision.journeyId, kind: 'inmemory',
          });
        } else if (restorePlan.action === RESTORE_ACTIONS.RESTORE_PERSISTED) {
          this._emit(RESOLUTION_ACTIVATION_EVENTS.JOURNEY_RESTORATION_REQUESTED, {
            requestId, journeyId: resolutionDecision.journeyId, kind: 'persisted',
          });
        }
        const outcome = await executeRestoration({
          engine: this._engine,
          persistenceService: this._persistence,
          planResult: restorePlan,
          journeyId: resolutionDecision.journeyId,
        });
        initialState = outcome.currentState;
        if (outcome.kind === RESTORE_ACTIONS.RESUME_INMEMORY) {
          status = ACTIVATION_STATUSES.RESUMED;
          restorationStatus = RESTORATION_STATUSES.RESUMED_FROM_MEMORY;
          this._emit(RESOLUTION_ACTIVATION_EVENTS.JOURNEY_RESTORED, {
            requestId, journeyId: resolutionDecision.journeyId, kind: 'inmemory',
          });
        } else if (outcome.kind === RESTORE_ACTIONS.RESTORE_PERSISTED) {
          status = ACTIVATION_STATUSES.RESTORED;
          restorationStatus = RESTORATION_STATUSES.RESTORED_FROM_PERSISTENCE;
          this._emit(RESOLUTION_ACTIVATION_EVENTS.JOURNEY_RESTORED, {
            requestId, journeyId: resolutionDecision.journeyId, kind: 'persisted',
          });
        } else if (outcome.kind === RESTORE_ACTIONS.NEW_ACTIVATION) {
          status = ACTIVATION_STATUSES.ACTIVATED;
        }
      } catch (execErr) {
        this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_FAILED, {
          requestId, journeyId: resolutionDecision.journeyId,
          error: execErr.name, message: execErr.message,
        });
        const result = this._rejectedResult({
          request, resolutionDecision,
          status: ACTIVATION_STATUSES.FAILED,
          reason: execErr.message,
          restorationStatus: RESTORATION_STATUSES.REJECTED,
        });
        this._idempotency.set(requestId, result);
        return result;
      }

      // WS11 — Activation Result. Founder Ruling A2: no engine object.
      const result = makeActivationResult({
        requestId,
        resolutionDecisionId: resolutionDecision.resolutionDecisionId,
        journeyInstanceId: buildJourneyInstanceId(
          (request.principalContext && request.principalContext.principalRef) || null,
          resolutionDecision.journeyId,
        ),
        journeyId: resolutionDecision.journeyId,
        journeyVersion: resolutionDecision.journeyVersion,
        status,
        initialState,
        principalRef: (request.principalContext && request.principalContext.principalRef) || null,
        idempotencyStatus: IDEMPOTENCY_STATUSES.FIRST,
        conflictStatus: 'none',
        restorationStatus,
        reason: 'OK',
        nowFn: this._now,
        auditMetadata: {
          gatesPassed: 13,
          restorationAction: restorePlan.action,
        },
      });

      this._emit(RESOLUTION_ACTIVATION_EVENTS.ACTIVATION_COMPLETED, {
        requestId, journeyId: resolutionDecision.journeyId, status,
      });
      this._idempotency.set(requestId, result);
      return result;
    } finally {
      this._idempotency.endInFlight(requestId);
    }
  }
}

export { ActivationService };
